// Gantt view of the core (stage D; owner um/2026-10-23/05 п. 8, um/2026-10-30/06): any type with start and end dates — bars on a
// pixel-exact timeline (days, weeks, months, quarters), a milestone for a record with only an end, dependency lines, today's line,
// a resizable list on the left in one scroller with the timeline (rows can't drift apart), dragging a bar or its edge by whole days.

import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { avatarUrl, canWrite, recordTitle, type Id, type ViewDef } from '@confdeck/core-shared';
import { useAccess, useMeta, useSync, useTypesTick } from '../sync/react.tsx';
import { setFields } from '../sync/mutations.ts';
import { Icon } from '../ui/icons.tsx';
import { IconButton, Avatar, Button, cx, RecordChip, Segmented, Select, useNarrow } from '../ui/primitives.tsx';
import { bands, barBox, DAY, dayAt, dayOf, depRoute, isoOf, PX, rangeLabel, roundedPath, shiftRange, timeRange, todayUtc, weekends, xOf, ZOOM_LABEL, ZOOMS, type DragMode, type Zoom } from './scale.ts';
import { useBarDrag } from './useBarDrag.ts';
import { ColResizer, colWidth, useColWidth } from '../ui/ColWidth.tsx';
import s from './gantt.module.css';

const ROW = 36, HEAD = 48, BAR = 22;
// The width of the list: dragged by its right border, one for every Gantt, kept in this browser (the frame's drag edge, ui/ColWidth).
const LIST_W = colWidth({ key: 'confdeck.gantt.list', min: 180, max: 480, fallback: 280 });

type Row = {
  id: Id; title: string; start: number | null; end: number | null; raw: { start: unknown; end: unknown };
  color: string; progress: number; done: boolean; lost: boolean; deps: Id[]; who: { name: string; color: string; avatar?: string | null } | null;
};

// Width of a title in the bar font, to put the label inside the bar or beside it without cutting it.
let ctx: CanvasRenderingContext2D | null = null;
const textWidth = (t: string) => {
  ctx ??= document.createElement('canvas').getContext('2d');
  if (!ctx) return t.length * 7;
  // The bar's font: --t-meta at the emphasis weight, read from the same tokens the CSS uses.
  const root = getComputedStyle(document.documentElement);
  ctx.font = `${root.getPropertyValue('--w-emphasis').trim() || 500} ${root.getPropertyValue('--fs-small').trim() || '12px'} ${getComputedStyle(document.body).fontFamily}`;
  return ctx.measureText(t).width;
};
/** A dragged date written back in the stored form: a datetime keeps its time. */
const withDay = (orig: unknown, t: number) => (typeof orig === 'string' && orig.length > 10 ? isoOf(t) + orig.slice(10) : isoOf(t));

export function Gantt({ typeId, view, ids, onOpen, openId = null }: { typeId: Id; view: ViewDef; ids: Id[]; onOpen: (id: Id, e?: React.MouseEvent | React.KeyboardEvent) => void; openId?: Id | null }) {
  const meta = useMeta();
  const client = useSync();
  const access = useAccess();
  useTypesTick([typeId]);
  const narrow = useNarrow(640);
  const [zoom, setZoomState] = useState<Zoom>('day');
  const [listOpen, setListOpen] = useState(!narrow);
  const listW = useColWidth(LIST_W);
  const [hover, setHover] = useState<Id | null>(null);
  const [vp, setVp] = useState({ left: 0, width: 1200 });
  const scroller = useRef<HTMLDivElement>(null);
  const anchor = useRef<number | null>(null);
  const order = useRef<{ set: string; rank: Map<Id, number> }>({ set: '', rank: new Map() });
  const marker = useId().replace(/:/g, '');
  const g = view.gantt;
  const px = PX[zoom];
  const writable = canWrite(access, typeId);
  const list = listOpen ? listW : 0;
  const depField = g?.depends ? meta.field(typeId, g.depends) : undefined;
  const status = meta.fields(typeId).find((f) => f.fieldType === 'taxonomy' && f.required && meta.taxonomy(f.options.taxonomyId)?.style === 'pipeline');
  const whoField = view.columns.filter((c) => !c.hidden).map((c) => meta.field(typeId, c.field)).find((f) => f?.fieldType === 'user' && !f.options.multiple);
  const today = todayUtc();

  const { rows, undated } = useMemo(() => {
    const rows: Row[] = [];
    const undated: { id: Id; title: string }[] = [];
    for (const id of ids) {
      const r = client.store.record(id);
      if (!r || !g) continue;
      const start = dayOf(r.data[g.start]), end = dayOf(r.data[g.end]);
      const title = recordTitle(r, meta);
      if (start === null && end === null) { undated.push({ id, title }); continue; }
      // Colour and progress come from the stage: the share of the pipeline's open stages passed, a won stage is all of it.
      const term = status ? meta.term(String(r.data[status.key] ?? '')) : undefined;
      const open = term ? meta.children(term.parentId, term.taxonomyId).filter((t) => !t.final) : [];
      const progress = term?.final === 'won' ? 1 : term && !term.final && open.length ? open.findIndex((t) => t.id === term.id) / open.length : 0;
      const u = whoField ? meta.user(String(r.data[whoField.key] ?? '')) : undefined;
      rows.push({
        id, title, start, end, raw: { start: r.data[g.start], end: r.data[g.end] }, color: term?.color ?? 'blue', progress,
        done: term?.final === 'won', lost: term?.final === 'lost', deps: depField ? client.store.linked(r, depField) : [], who: u ? { name: u.name, color: u.color, avatar: u.avatar } : null,
      });
    }
    // Rows go by date, but the order is kept while the same records are shown: a dragged bar stays in its row under the pointer.
    const byDate = (a: Row, b: Row) => (a.start ?? a.end!) - (b.start ?? b.end!) || (a.end ?? a.start!) - (b.end ?? b.start!);
    const set = rows.map((r) => r.id).sort().join();
    if (order.current.set !== set) order.current = { set, rank: new Map([...rows].sort(byDate).map((r, i) => [r.id, i])) };
    const rank = order.current.rank;
    rows.sort((a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity) || byDate(a, b));
    return { rows, undated };
  }, [ids, g, client.store, meta, depField, status, whoField, client.store.typeVersion.get(typeId)]);

  const range = useMemo(() => timeRange(rows.flatMap((r) => [r.start, r.end]).filter((x): x is number => x !== null), today, zoom, Math.ceil(vp.width / px) + 1),
    [rows, today, zoom, px, vp.width]);
  const { from, days } = range;
  const head = useMemo(() => bands(range.from, range.to, zoom, today), [range, zoom, today]);
  const offs = useMemo(() => weekends(range.from, range.to, zoom), [range, zoom]);
  const width = xOf(from + days * DAY, from, px);
  const height = rows.length * ROW;

  const commit = useCallback((id: Id, mode: DragMode, delta: number) => {
    const r = rows.find((x) => x.id === id), rec = client.store.record(id);
    if (!r || !rec || !g) return;
    const next = shiftRange({ start: r.start ?? r.end!, end: r.end ?? r.start! }, mode, delta);
    const data: Record<string, unknown> = {};
    if (r.start !== null && next.start !== r.start) data[g.start] = withDay(r.raw.start, next.start);
    if (r.end !== null ? next.end !== r.end : next.end !== next.start) data[g.end] = withDay(r.raw.end, next.end);
    // Conditional on the record's rev, so a drop does not overwrite dates changed elsewhere meanwhile; while an earlier edit of this
    // bar is still unconfirmed its new rev is unknown, and the edit goes unconditionally (a quick second ← must not be refused).
    const inFlight = client.store.pending.some((tx) => tx.ops.some((o) => o.id === id));
    if (Object.keys(data).length) setFields(client, rec, data, inFlight ? {} : { rev: rec.rev });
  }, [rows, client, g]);
  const { drag, start, dragged } = useBarDrag(scroller, px, list, commit);

  // The scroller reports what part of the timeline is seen: chevrons for bars out of view, the anchor of a zoom change.
  // The list width is read from a ref: dragging the list border must not re-subscribe the observers on every frame.
  const listRef = useRef(list);
  listRef.current = list;
  const measure = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const w = el.clientWidth - listRef.current;
    setVp((v) => (v.left === el.scrollLeft && v.width === w ? v : { left: el.scrollLeft, width: w }));
  }, []);
  useLayoutEffect(measure, [list, measure]);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    el.addEventListener('scroll', onScroll, { passive: true });
    measure();
    return () => { ro.disconnect(); el.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, [measure]);

  // First paint: today a quarter into the view. A zoom change keeps the day in the middle of the view in the middle.
  const placed = useRef(false);
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || !g) return;
    const w = el.clientWidth - list;
    if (anchor.current !== null) { el.scrollLeft = xOf(anchor.current, from, px) - w / 2; anchor.current = null; }
    else if (!placed.current && rows.length) { el.scrollLeft = xOf(today, from, px) - w / 4; placed.current = true; }
  }, [zoom, from, px, rows.length, today, list, g]);
  const setZoom = (z: Zoom) => {
    const el = scroller.current;
    if (el) anchor.current = dayAt(el.scrollLeft + (el.clientWidth - list) / 2, from, px);
    setZoomState(z);
  };
  const scrollTo = (x: number, behavior: ScrollBehavior = 'smooth') => scroller.current?.scrollTo({ left: Math.max(0, x), behavior });

  if (!g) return <div className={s.empty}>У вида не выбраны поля начала и конца.</div>;
  const index = new Map(rows.map((r, i) => [r.id, i]));
  const shown = (r: Row) => {
    const base = { start: r.start ?? r.end!, end: r.end ?? r.start! };
    return drag?.id === r.id ? shiftRange(base, drag.mode, drag.delta) : base;
  };
  const geom = (r: Row) => {
    const d = shown(r), box = barBox(d.start, d.end, from, px);
    return { ...d, ...box, milestone: r.start === null };
  };
  const boxes = rows.map(geom);
  const todayX = xOf(today, from, px) + Math.round(px / 2);
  const active = drag?.id ?? hover;
  const typeName = meta.type(typeId)?.nameMany ?? '';

  // The date hint stands over the edge being dragged, or over the middle of a moved bar, and stays inside the seen timeline.
  const hintX = (b: { left: number; width: number }, mode: DragMode) =>
    Math.min(Math.max(mode === 'start' ? b.left : mode === 'end' ? b.left + b.width : b.left + b.width / 2, vp.left + 70), Math.max(vp.left + 70, vp.left + vp.width - 70));
  const onBarKey = (e: React.KeyboardEvent, r: Row, milestone: boolean) => {
    if (!writable || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
    e.preventDefault();
    const d = e.key === 'ArrowLeft' ? -1 : 1;
    commit(r.id, e.shiftKey && !milestone ? 'end' : e.altKey && !milestone ? 'start' : 'move', d);
  };

  return (
    <div className={s.wrap} data-gantt style={{ '--g-row': `${ROW}px`, '--g-head': `${HEAD}px`, '--g-bar': `${BAR}px`, '--g-list': `${list}px` } as React.CSSProperties}>
      <div className={s.toolbar}>
        <IconButton size="sm" icon="sidebar" pressed={listOpen} label={listOpen ? 'Скрыть список' : 'Показать список'} onClick={() => setListOpen((o) => !o)} />
        {narrow
          ? <Select className={s.zoomSelect} label="Масштаб" value={zoom} onChange={(v) => setZoom(v as Zoom)} options={ZOOMS.map((z) => ({ value: z, label: ZOOM_LABEL[z] }))} />
          : (
            <Segmented size="sm" label="Масштаб" value={zoom} onChange={setZoom} items={ZOOMS.map((z) => ({ value: z, label: ZOOM_LABEL[z] }))} />
          )}
        <Button size="sm" variant="secondary" icon="date" aria-label="Сегодня" onClick={() => scrollTo(todayX - vp.width / 3)}>Сегодня</Button>
      </div>
      <div ref={scroller} className={s.scroll}>
        <div className={s.canvas} style={{ width: list + width, height: HEAD + height }}>
          {listOpen && (
            <div className={s.corner}>
              <span className={s.cornerName}>{typeName}</span><span className={s.count}>{rows.length}</span>
              <ColResizer width={LIST_W} label="Ширина списка" />
            </div>
          )}
          <div className={s.scale} style={{ width }} key={`h-${zoom}`}>
            <div className={s.bandTop}>{head.top.map((c) => <div key={c.key} className={s.topCell} style={{ left: c.left, width: c.width }}><span>{c.label}</span></div>)}</div>
            <div className={s.bandBottom}>
              {head.bottom.map((c) => (
                <div key={c.key} className={cx(s.cell, c.weekend && s.cellOff)} style={{ left: c.left, width: c.width }}>
                  <span className={cx(c.today && s.cellToday)}>{c.label}</span>
                </div>
              ))}
            </div>
            <span className={s.todayDot} style={{ left: todayX }} />
          </div>
          {listOpen && (
            <div className={s.list} style={{ height }}>
              {rows.map((r) => (
                <div key={r.id} className={cx(s.item, (active === r.id || openId === r.id) && s.itemOn)} onMouseEnter={() => setHover(r.id)} onMouseLeave={() => setHover(null)}>
                  <span className={s.dot} style={{ background: `var(--c-${r.color}-dot)` }} />
                  <button type="button" className={cx(s.itemTitle, r.lost && s.struck)} onClick={(e) => onOpen(r.id, e)} title={r.title}>{r.title}</button>
                  {r.who && <Avatar name={r.who.name} color={r.who.color} src={avatarUrl(r.who)} size={20} />}
                </div>
              ))}
              <ColResizer width={LIST_W} label="Ширина списка" hidden />
            </div>
          )}
          <div className={s.body} style={{ width, height }} key={`b-${zoom}`}>
            {offs.map((o) => <div key={o.left} className={s.weekend} style={{ left: o.left, width: o.width }} />)}
            {head.bottom.map((c) => <div key={c.key} className={s.vline} style={{ left: c.left }} />)}
            {head.top.map((c) => <div key={c.key} className={s.vlineMajor} style={{ left: c.left }} />)}
            {rows.map((r, i) => <div key={r.id} className={cx(s.lane, active === r.id && s.laneOn)} style={{ top: i * ROW }} onMouseEnter={() => setHover(r.id)} onMouseLeave={() => setHover(null)} />)}
            <div className={s.today} style={{ left: todayX }} />
            <svg className={s.deps} width={width} height={height} aria-hidden>
              <defs>
                {(['', 'On'] as const).map((k) => (
                  <marker key={k} id={`${marker}${k}`} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0.5 L7.5,4 L0,7.5 z" className={k ? s.headOn : s.head} />
                  </marker>
                ))}
              </defs>
              {rows.flatMap((r, i) => r.deps.filter((d) => index.has(d)).map((d) => {
                const j = index.get(d)!, a = boxes[j]!, b = boxes[i]!;
                const x1 = a.milestone ? a.left + a.width / 2 + 8 : a.left + a.width, x2 = b.milestone ? b.left + b.width / 2 - 8 : b.left;
                const on = active === r.id || active === d;
                return <path key={`${d}-${r.id}`} d={roundedPath(depRoute(x1, j * ROW + ROW / 2, x2 - 1, i * ROW + ROW / 2, ROW))} className={cx(s.dep, on && s.depOn)} markerEnd={`url(#${marker}${on ? 'On' : ''})`} />;
              }))}
            </svg>
            {rows.map((r, i) => {
              const b = boxes[i]!, top = i * ROW, dragging = drag?.id === r.id;
              const off = b.left + b.width < vp.left ? 'left' : b.left > vp.left + vp.width ? 'right' : null;
              // A bar that starts left of the view keeps its title at the view's edge; when the seen part is too short, the title goes beside.
              const shift = Math.max(0, vp.left - b.left);
              const label = r.title, tw = textWidth(label), inside = !b.milestone && !off && tw + 20 <= b.width - shift;
              const tone = { '--bar-bg': `var(--c-${r.color}-bg)`, '--bar-fg': `var(--c-${r.color}-fg)`, '--bar-dot': `var(--c-${r.color}-dot)`, '--p': `${Math.round(r.progress * 100)}%` } as React.CSSProperties;
              const aria = `${r.title}: ${rangeLabel(b.start, b.end)}`;
              return (
                <div key={r.id} className={s.row} style={{ top, ...tone }}>
                  {b.milestone ? (
                    <button type="button" data-id={r.id} className={cx(s.milestone, dragging && s.dragging, writable && s.grab)} style={{ left: b.left + b.width / 2 }} aria-label={aria} title={aria}
                      onPointerDown={writable ? (e) => start(e, r.id, 'move') : undefined} onClick={(e) => { if (!dragged.current) onOpen(r.id, e); }} onKeyDown={(e) => onBarKey(e, r, true)}
                      onMouseEnter={() => setHover(r.id)} onMouseLeave={() => setHover(null)} />
                  ) : (
                    <button type="button" data-id={r.id} className={cx(s.bar, dragging && s.dragging, writable && s.grab, r.done && s.done, r.lost && s.lost)} style={{ left: b.left, width: b.width }} aria-label={aria}
                      onPointerDown={writable ? (e) => start(e, r.id, 'move') : undefined} onClick={(e) => { if (!dragged.current) onOpen(r.id, e); }} onKeyDown={(e) => onBarKey(e, r, false)}
                      onMouseEnter={() => setHover(r.id)} onMouseLeave={() => setHover(null)}>
                      <span className={s.progress} />
                      {inside && <span className={s.barLabel} style={shift ? { transform: `translateX(${shift}px)` } : undefined}>{r.done && <Icon name="tick" size={12} />}{label}</span>}
                      {writable && <>
                        <span className={cx(s.edge, s.edgeStart)} onPointerDown={(e) => start(e, r.id, 'start')} aria-hidden />
                        <span className={cx(s.edge, s.edgeEnd)} onPointerDown={(e) => start(e, r.id, 'end')} aria-hidden />
                      </>}
                    </button>
                  )}
                  {!inside && <span className={cx(s.outLabel, r.lost && s.struck, off === 'left' && s.outLabelAway)} style={{ left: off === 'left' ? vp.left + 36 : b.milestone ? b.left + b.width / 2 + 14 : b.left + b.width + 8 }}>{label}</span>}
                  {dragging && <span className={cx(s.hint, i === 0 && s.hintBelow)} style={{ left: hintX(b, drag.mode) }}>{rangeLabel(b.start, b.end)}</span>}
                  {off && !dragging && (
                    <button type="button" className={cx(s.jump, active === r.id && s.jumpOn)} style={{ left: off === 'left' ? vp.left + 6 : vp.left + vp.width - 30 }}
                      aria-label={`Показать «${r.title}»`} title={rangeLabel(b.start, b.end)} onClick={() => scrollTo(b.left - 48)}>
                      <Icon name={off === 'left' ? 'chevronLeft' : 'chevronRight'} size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {undated.length > 0 && (
        <div className={s.undated}>
          <div className={s.undatedHead}>Без сроков: {undated.length}</div>
          <div className={s.chips}>{undated.map((u) => <RecordChip key={u.id} onClick={(e) => onOpen(u.id, e)}>{u.title}</RecordChip>)}</div>
        </div>
      )}
    </div>
  );
}
